import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import Paragraph from '@tiptap/extension-paragraph';
import { useEffect } from 'react';
import './rich-text-editor.modules.scss';

export type RichTextEditorProps = {
    value?: string;
    placeholder?: string;
    ariaLabel?: string;
    disabled?: boolean;
    onChange?: (value: string) => void;
    onBlur?: () => void;
    id?: string;
};

const ToolbarButton = ({
    onClick,
    isActive,
    disabled,
    children,
    title,
}: {
    onClick: () => void;
    isActive: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title: string;
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`rich-text-toolbar__button ${isActive ? 'rich-text-toolbar__button--active' : ''}`}
        title={title}
    >
        {children}
    </button>
);

const Toolbar = ({ editor, disabled }: { editor: Editor | null; disabled?: boolean }) => {
    if (!editor) return null;

    return (
        <div className="rich-text-toolbar">
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={editor.isActive('bold')}
                disabled={disabled}
                title="Bold"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z" />
                </svg>
            </ToolbarButton>
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={editor.isActive('italic')}
                disabled={disabled}
                title="Italic"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
                </svg>
            </ToolbarButton>
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleHighlight().run()}
                isActive={editor.isActive('highlight')}
                disabled={disabled}
                title="Highlight"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.24 2.59L17.41 4.76L10.41 11.76L8.24 9.59L15.24 2.59ZM3 17V21H7L15.12 12.88L11.12 8.88L3 17ZM5 19V17.41L11.12 11.29L12.71 12.88L6.59 19H5Z" />
                </svg>
            </ToolbarButton>
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                isActive={editor.isActive('bulletList')}
                disabled={disabled}
                title="Bullet List"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z" />
                </svg>
            </ToolbarButton>
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                isActive={editor.isActive('blockquote')}
                disabled={disabled}
                title="Blockquote"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
                </svg>
            </ToolbarButton>
        </div>
    );
};

const RichTextEditor = ({
    value = '',
    placeholder,
    ariaLabel,
    disabled = false,
    onChange,
    onBlur,
    id,
}: RichTextEditorProps) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Disable features we don't need
                heading: false,
                codeBlock: false,
                orderedList: false,
                horizontalRule: false,
                strike: false,
            }),
            Paragraph,
            Highlight.configure({ multicolor: false }),
            Placeholder.configure({
                placeholder: placeholder || '',
            }),
        ],
        content: value,
        editable: !disabled,
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        onBlur: () => {
            onBlur?.();
        },
        editorProps: {
            attributes: {
                'aria-label': ariaLabel || '',
                role: 'textbox',
            },
        },
    });

    // Update editor content when value prop changes externally
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value);
        }
    }, [value, editor]);

    // Update editable state when disabled prop changes
    useEffect(() => {
        if (editor) {
            editor.setEditable(!disabled);
        }
    }, [disabled, editor]);

    return (
        <div className={`rich-text-editor ${disabled ? 'rich-text-editor--disabled' : ''}`} id={id}>
            {!disabled && <Toolbar editor={editor} disabled={disabled} />}
            <EditorContent editor={editor} className="rich-text-editor__content" />
        </div>
    );
};

export default RichTextEditor;
